import { customElement, attachShadowTemplate, coreProperty, coerceBoolean, coerceNumberArray, coerceNumber } from '@tylertech/forge-core';
import { tylIconFirstPage, tylIconLastPage, tylIconKeyboardArrowRight, tylIconKeyboardArrowLeft } from '@tylertech/tyler-icons/standard';
import { PAGINATOR_CONSTANTS, IPaginatorChangeEventData, PaginatorRangeLabelBuilder } from './paginator-constants';
import { PaginatorCore } from './paginator-core';
import { PaginatorAdapter } from './paginator-adapter';
import { IconButtonComponent } from '../icon-button/icon-button';
import { SelectComponent } from '../select/select';
import { IconRegistry } from '../icon/icon-registry';
import { BaseComponent, IBaseComponent } from '../core/base/base-component';
import { TooltipComponent } from '../tooltip/tooltip';

const template = '<template><div class=\"forge-paginator\" part=\"root\"><div class=\"container\" part=\"container\"><div class=\"label\" part=\"label\" id=\"label\"><slot name=\"label\"></slot></div><forge-select class=\"page-size-options\" aria-labelledby=\"label\" label-position=\"none\" density=\"extra-small\" part=\"page-size-options\"></forge-select><div class=\"range-label\" part=\"range-label\"><slot name=\"range-label\"></slot></div></div><div class=\"actions\" part=\"actions\"><div id=\"first-page-container\"><forge-icon-button class=\"first-page\" part=\"first-page-button\" part=\"first-page-button-element\"><forge-icon name=\"first_page\" part=\"first-page-icon\"></forge-icon></forge-icon-button><forge-tooltip type=\"label\" placement=\"top\"><slot name=\"first-page-tooltip\">Go to the first page</slot></forge-tooltip></div><div id=\"previous-page-container\"><forge-icon-button class=\"previous-page\" part=\"previous-page-button\" part=\"previous-page-button-element\"><forge-icon name=\"keyboard_arrow_left\" part=\"previous-page-button-icon\"></forge-icon></forge-icon-button><forge-tooltip type=\"label\" placement=\"top\"><slot name=\"previous-page-tooltip\">Go to the previous page</slot></forge-tooltip></div><div class=\"alternative-range-label\" part=\"alternative-range-label\"><slot name=\"alternative-range-label\"></slot></div><div id=\"next-page-container\"><forge-icon-button class=\"next-page\" part=\"next-page-button\" part=\"next-page-button-element\"><forge-icon name=\"keyboard_arrow_right\" part=\"next-page-icon\"></forge-icon></forge-icon-button><forge-tooltip type=\"label\" placement=\"top\"><slot name=\"next-page-tooltip\">Go to the next page</slot></forge-tooltip></div><div id=\"last-page-container\"><forge-icon-button class=\"last-page\" part=\"last-page-button\" part=\"last-page-button-element\"><forge-icon name=\"last_page\" part=\"last-page-icon\"></forge-icon></forge-icon-button><forge-tooltip type=\"label\" placement=\"top\"><slot name=\"last-page-tooltip\">Go to the last page</slot></forge-tooltip></div></div></div></template>';
const styles = ':host{display:block}:host([hidden]){display:none}.forge-paginator{display:-webkit-box;display:flex;gap:var(--forge-spacing-xsmall,8px)}.container{display:-webkit-box;display:flex;gap:var(--forge-spacing-medium,16px);-webkit-box-align:center;align-items:center}.alternative-range-label,.label,.range-label{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;font-family:var(--forge-typography-label1-font-family, var(--forge-typography-font-family, \"Roboto\", sans-serif));font-size:var(--forge-typography-label1-font-size, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-label-font-size-scale, .75)));font-weight:var(--forge-typography-label1-font-weight,400);line-height:var(--forge-typography-label1-line-height, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-label-line-height-scale, 1.25)));letter-spacing:var(--forge-typography-label1-letter-spacing, .0357142857em);text-transform:var(--forge-typography-label1-text-transform,inherit);-webkit-text-decoration:var(--forge-typography-label1-text-decoration,inherit);text-decoration:var(--forge-typography-label1-text-decoration,inherit)}.actions{display:-webkit-box;display:flex;-webkit-box-align:center;align-items:center}.alternative-range-label{display:none}:host([alternative]) .alternative-range-label{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;font-family:var(--forge-typography-body1-font-family, var(--forge-typography-font-family, \"Roboto\", sans-serif));font-size:var(--forge-typography-body1-font-size, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-body-font-size-scale, .875)));font-weight:var(--forge-typography-body1-font-weight,400);line-height:var(--forge-typography-body1-line-height, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-body-line-height-scale, 1.125)));letter-spacing:var(--forge-typography-body1-letter-spacing, .0357142857em);text-transform:var(--forge-typography-body1-text-transform,inherit);-webkit-text-decoration:var(--forge-typography-body1-text-decoration,inherit);text-decoration:var(--forge-typography-body1-text-decoration,inherit);display:inline-block}:host([alternative]) .container{display:none}:host([alternative]) .actions{width:100%;-webkit-box-pack:var(--forge-paginator-alternative-alignment,start);justify-content:var(--forge-paginator-alternative-alignment,start)}';

export interface IPaginatorComponent extends IBaseComponent {
  pageIndex: number;
  pageSize: number;
  offset: number;
  total: number;
  pageSizeOptions: number[];
  label: string;
  firstLast: boolean;
  first: boolean;
  disabled: boolean;
  alternative: boolean;
  rangeLabelCallback: PaginatorRangeLabelBuilder;
}

declare global {
  interface HTMLElementTagNameMap {
    'forge-paginator': IPaginatorComponent;
  }

  interface HTMLElementEventMap {
    'forge-paginator-change': CustomEvent<IPaginatorChangeEventData>;
  }
}

/**
 * @tag forge-paginator
 *
 * @event {CustomEvent<IPaginatorChangeEventData>} forge-paginator-change - Dispatched when the paginator state changes.
 */
@customElement({
  name: PAGINATOR_CONSTANTS.elementName,
  dependencies: [IconButtonComponent, SelectComponent, TooltipComponent]
})
export class PaginatorComponent extends BaseComponent implements IPaginatorComponent {
  public static get observedAttributes(): string[] {
    return Object.values(PAGINATOR_CONSTANTS.observedAttributes);
  }

  private _core: PaginatorCore;

  constructor() {
    super();
    IconRegistry.define([tylIconFirstPage, tylIconKeyboardArrowLeft, tylIconKeyboardArrowRight, tylIconLastPage]);
    attachShadowTemplate(this, template, styles);
    this._core = new PaginatorCore(new PaginatorAdapter(this));
  }

  public connectedCallback(): void {
    this._core.initialize();
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    switch (name) {
      case PAGINATOR_CONSTANTS.observedAttributes.PAGE_INDEX:
        this.pageIndex = coerceNumber(newValue) ?? PAGINATOR_CONSTANTS.numbers.DEFAULT_PAGE_INDEX;
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.PAGE_SIZE:
        this.pageSize = coerceNumber(newValue) ?? PAGINATOR_CONSTANTS.numbers.DEFAULT_PAGE_SIZE;
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.OFFSET:
        this.offset = coerceNumber(newValue);
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.TOTAL:
        this.total = coerceNumber(newValue) ?? PAGINATOR_CONSTANTS.numbers.DEFAULT_TOTAL;
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.PAGE_SIZE_OPTIONS:
        this.pageSizeOptions = coerceNumberArray(newValue) ?? PAGINATOR_CONSTANTS.numbers.DEFAULT_PAGE_SIZE_OPTIONS;
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.LABEL:
        this.label = newValue;
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.FIRST_LAST:
        this.firstLast = coerceBoolean(newValue);
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.FIRST:
        this.first = coerceBoolean(newValue);
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.DISABLED:
        this.disabled = coerceBoolean(newValue);
        break;
      case PAGINATOR_CONSTANTS.observedAttributes.ALTERNATIVE:
        this.alternative = coerceBoolean(newValue);
        break;
    }
  }

  /**
   * The zero-based page index.
   * @attribute page-index
   * @default 0
   */
  @coreProperty()
  public declare pageIndex: number;

  /**
   * Number of items to display on a page.
   * @attribute page-size
   * @default 25
   */
  @coreProperty()
  public declare pageSize: number;

  /**
   * Sets page index by providing the number of items to skip. The getter for this property returns the number of items to skip.
   * @attribute offset
   * @default 0
   */
  @coreProperty()
  public declare offset: number;

  /**
   * The total number of items to be paginated.
   * @attribute
   * @default 0;
   */
  @coreProperty()
  public declare total: number;

  /**
   * The set of provided page size options to display to the user.
   * @attribute page-size-options
   * @default [5, 15, 25, 50, 100]
   */
  @coreProperty()
  public declare pageSizeOptions: number[];

  /**
   * A label for the paginator.
   * @attribute
   * @default "Rows per page:"
   */
  @coreProperty()
  public declare label: string;

  /**
   * Whether to show the first page and last page buttons.
   * @attribute first-last
   * @default false
   */
  @coreProperty()
  public declare firstLast: boolean;

  /**
   * Whether to show the first page button. Default is false.
   * @attribute
   * @default false
   */
  @coreProperty()
  public declare first: boolean;

  /**
   * Whether the paginator is disabled.
   * @attribute
   * @default false
   */
  @coreProperty()
  public declare disabled: boolean;

  /**
   * Whether to use the alternative range label slot.
   * @attribute
   * @default false
   */
  @coreProperty()
  public declare alternative: boolean;

  /**
   * A callback function to build the range label dynamically.
   */
  @coreProperty()
  public declare rangeLabelCallback: PaginatorRangeLabelBuilder;

  /** Sets focus to the first focusable element within the paginator. */
  public override focus(options?: FocusOptions): void {
    this._core.focus(options);
  }
}
